import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Button from './Button';
import LocationInput from './LocationInput';
import VendorCard from './VendorCard';
import vendorService from '../../services/vendorService';
import { useDebounce } from '../../hooks/useDebounce';
import { VENDOR_CATEGORIES } from '../../constants';

const SearchHeader = ({ variant = 'default', onSearch, onLocationChange, placeholder = "Search for vendors, services, or locations (e.g. Henna artist, DJ, decorator in NYC)" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [vendorListings, setVendorListings] = useState([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [vendorError, setVendorError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce search query to prevent excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Enhanced vendor categories with cultural context
  const vendorCategorySuggestions = [
    { type: 'service', label: 'DJ Services', cultural: 'Bollywood & Punjabi Music', icon: 'Music', category: VENDOR_CATEGORIES?.DJ },
    { type: 'service', label: 'Mehndi Artists', cultural: 'Henna & Bridal Specialists', icon: 'Palette', category: VENDOR_CATEGORIES?.MEHNDI },
    { type: 'service', label: 'Caterers', cultural: 'Indian Cuisine Specialists', icon: 'ChefHat', category: VENDOR_CATEGORIES?.CATERING },
    { type: 'service', label: 'Wedding Photographers', cultural: 'Ceremony & Reception', icon: 'Camera', category: VENDOR_CATEGORIES?.PHOTOGRAPHY },
    { type: 'service', label: 'Makeup Artists', cultural: 'Bridal & Party Makeup', icon: 'Sparkles', category: VENDOR_CATEGORIES?.MAKEUP },
    { type: 'service', label: 'Mandap Decorators', cultural: 'Sacred Canopy & Venue Setup', icon: 'Home', category: VENDOR_CATEGORIES?.MANDAP },
    { type: 'service', label: 'Wedding Venues', cultural: 'Banquet Halls & Gardens', icon: 'Building', category: VENDOR_CATEGORIES?.VENUES },
    { type: 'service', label: 'Dhol Players', cultural: 'Traditional Drummers', icon: 'Drum', category: VENDOR_CATEGORIES?.DHOL },
    { type: 'service', label: 'Wedding Priests', cultural: 'Hindu & Sikh Ceremonies', icon: 'User', category: VENDOR_CATEGORIES?.PRIEST },
    { type: 'service', label: 'Lehenga Designers', cultural: 'Bridal Wear & Alterations', icon: 'Shirt', category: VENDOR_CATEGORIES?.ATTIRE },
    { type: 'service', label: 'Videographers', cultural: 'Wedding Films & Highlights', icon: 'Video', category: VENDOR_CATEGORIES?.VIDEOGRAPHY },
    { type: 'service', label: 'Rangoli Artists', cultural: 'Floor Art & Decorations', icon: 'Sparkles', category: VENDOR_CATEGORIES?.RANGOLI },
    { type: 'service', label: 'Florists', cultural: 'Garlands & Floral Arrangements', icon: 'Flower', category: VENDOR_CATEGORIES?.FLORIST },
    { type: 'service', label: 'Wedding Planners', cultural: 'Full Service Coordination', icon: 'Calendar', category: VENDOR_CATEGORIES?.PLANNING },
    { type: 'service', label: 'Traditional Musicians', cultural: 'Classical & Folk Music', icon: 'Music', category: VENDOR_CATEGORIES?.MUSICIANS },
    { type: 'service', label: 'Jewelry Designers', cultural: 'Bridal & Custom Jewelry', icon: 'Gem', category: VENDOR_CATEGORIES?.JEWELRY },
    { type: 'service', label: 'Transportation', cultural: 'Baraat & Guest Transport', icon: 'Car', category: VENDOR_CATEGORIES?.TRANSPORTATION },
    { type: 'service', label: 'Invitation Designers', cultural: 'Custom Cards & Digital', icon: 'FileText', category: VENDOR_CATEGORIES?.INVITATIONS },
    { type: 'service', label: 'Decor Services', cultural: 'Stage & Venue Decoration', icon: 'Home', category: VENDOR_CATEGORIES?.DECOR }
  ];

  // Fetch vendor listings based on search query
  useEffect(() => {
    const fetchVendorListings = async () => {
      if (debouncedSearchQuery?.length > 2) {
        setIsLoadingVendors(true);
        setVendorError(null);
        
        try {
          const filters = {
            search: debouncedSearchQuery,
            limit: 6,
            verified: true
          };
          
          // Add location filter if available
          if (locationQuery?.trim()) {
            filters.city = locationQuery?.trim();
          }
          
          const result = await vendorService?.searchVendors(debouncedSearchQuery, filters);
          
          if (result?.success) {
            setVendorListings(result?.data || []);
          } else {
            setVendorError(result?.error || 'Failed to fetch vendor listings');
            setVendorListings([]);
          }
        } catch (error) {
          console.error('Error fetching vendor listings:', error);
          setVendorError('Failed to fetch vendor listings');
          setVendorListings([]);
        } finally {
          setIsLoadingVendors(false);
        }
      } else {
        setVendorListings([]);
        setVendorError(null);
      }
    };

    fetchVendorListings();
  }, [debouncedSearchQuery, locationQuery]);

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    
    if (value?.length > 0) {
      const filtered = vendorCategorySuggestions?.filter(
        suggestion => 
          suggestion?.label?.toLowerCase()?.includes(value?.toLowerCase()) ||
          suggestion?.cultural?.toLowerCase()?.includes(value?.toLowerCase())
      );
      setSuggestions(filtered?.slice(0, 5)); // Show top 5 matches as requested
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  };

  const handleLocationChange = (location) => {
    setLocationQuery(location);
    onLocationChange?.(location);
  };

  const handleLocationSelect = (locationData) => {
    setLocationQuery(locationData?.displayText);
    onLocationChange?.(locationData);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      onSearch?.(searchQuery?.trim(), locationQuery);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion?.label);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch?.(suggestion?.label, locationQuery);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions?.[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
    if (searchQuery?.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = (e) => {
    // Delay hiding suggestions to allow clicking
    setTimeout(() => {
      if (!suggestionsRef?.current?.contains(e?.relatedTarget)) {
        setShowSuggestions(false);
        if (variant === 'compact') {
          setIsExpanded(false);
        }
      }
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
        if (variant === 'compact') {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [variant]);

  if (variant === 'hero') {
    return (
      <div className="w-full max-w-4xl mx-auto" ref={searchRef}>
        <div className="relative">
          <form onSubmit={handleSearchSubmit} className="relative">
            {/* Desktop Layout */}
            <div className="hidden md:block">
              <div className="relative flex items-center bg-white rounded-2xl shadow-medium border border-border-light overflow-hidden">
                <div className="flex items-center pl-6 pr-4 py-4">
                  <Icon name="Search" size={24} color="#667eea" />
                </div>
                <Input
                  type="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="flex-1 border-none bg-transparent text-lg placeholder:text-text-muted focus:ring-0 focus:outline-none py-4"
                />
                <div className="w-px h-8 bg-border-light mx-2"></div>
                <LocationInput
                  value={locationQuery}
                  onChange={handleLocationChange}
                  onLocationSelect={handleLocationSelect}
                  placeholder="Where?"
                  variant="hero-desktop"
                />
                <div className="flex items-center space-x-2 pr-6">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    iconName="Search"
                    iconSize={20}
                    className="px-8"
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Mobile Layout */}
            <div className="md:hidden">
              <div className="space-y-3">
                {/* Search Input */}
                <div className="relative flex items-center bg-white rounded-2xl shadow-medium border border-border-light overflow-hidden">
                  <div className="flex items-center pl-4 pr-2 py-3">
                    <Icon name="Search" size={20} color="#667eea" />
                  </div>
                  <Input
                    type="search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder="Search vendors..."
                    className="flex-1 border-none bg-transparent text-base placeholder:text-text-muted focus:ring-0 focus:outline-none py-3 min-w-0"
                  />
                </div>
                
                {/* Location Input */}
                <div className="relative bg-white rounded-2xl shadow-medium border border-border-light overflow-hidden">
                  <LocationInput
                    value={locationQuery}
                    onChange={handleLocationChange}
                    onLocationSelect={handleLocationSelect}
                    placeholder="Enter city, state, or ZIP"
                    variant="hero-mobile"
                  />
                </div>
                
                {/* Search Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  iconName="Search"
                  iconSize={20}
                  className="w-full"
                >
                  Search
                </Button>
              </div>
            </div>
          </form>

          {/* Enhanced Hero Suggestions with Vendor Listings */}
          {showSuggestions && (suggestions?.length > 0 || vendorListings?.length > 0 || isLoadingVendors) && (
            <div 
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-strong border border-border-light z-50 overflow-hidden animate-slide-down"
            >
              <div className="max-h-96 overflow-y-auto">
                {/* Category Suggestions */}
                {suggestions?.length > 0 && (
                  <div className="p-2 border-b border-border-light">
                    <div className="text-xs font-medium text-text-secondary px-4 py-2">Vendor Categories</div>
                    <div className="max-h-64 overflow-y-auto">
                      {suggestions?.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-smooth text-left ${
                            index === selectedIndex 
                              ? 'bg-primary-50 text-primary border-primary border' :'hover:bg-surface-100'
                          }`}
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                            suggestion?.type === 'service' ? 'bg-primary-50 text-primary' : 'bg-secondary-50 text-secondary'
                          }`}>
                            <Icon name={suggestion?.icon} size={16} color="currentColor" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text-primary truncate">{suggestion?.label}</div>
                            <div className="text-sm text-text-secondary font-caption truncate">{suggestion?.cultural}</div>
                          </div>
                          <Icon name="ArrowUpRight" size={16} color="#667eea" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vendor Listings */}
                {(vendorListings?.length > 0 || isLoadingVendors) && (
                  <div className="p-2">
                    <div className="text-xs font-medium text-text-secondary px-4 py-2">Vendors</div>
                    {isLoadingVendors ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        <span className="ml-2 text-sm text-text-secondary">Loading vendors...</span>
                      </div>
                    ) : vendorError ? (
                      <div className="px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg mx-2">
                        {vendorError}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {vendorListings?.map((vendor, index) => (
                          <div key={vendor?.id || index} className="px-2">
                            <VendorCard 
                              vendor={vendor} 
                              compact={true} 
                              showSaveButton={false} 
                              onToggleComparison={() => {}}
                              isSelectedForComparison={false}
                              canAddToComparison={true}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={searchRef}>
        <div className={`flex items-center transition-all duration-200 ${
          isExpanded ? 'w-80' : 'w-10'
        }`}>
          {!isExpanded ? (
            <button
              onClick={() => setIsExpanded(true)}
              className="flex items-center justify-center w-10 h-10 rounded-lg text-text-secondary hover:text-primary hover:bg-surface-100 transition-smooth"
              aria-label="Open search"
            >
              <Icon name="Search" size={20} color="currentColor" />
            </button>
          ) : (
            <form onSubmit={handleSearchSubmit} className="w-full">
              <div className="relative flex items-center bg-white rounded-lg shadow-soft border border-border overflow-hidden">
                <div className="flex items-center pl-3 pr-2">
                  <Icon name="Search" size={18} color="#667eea" />
                </div>
                <Input
                  type="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="flex-1 border-none bg-transparent text-sm placeholder:text-text-muted focus:ring-0 focus:outline-none py-2"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsExpanded(false);
                    setSearchQuery('');
                    setShowSuggestions(false);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-text-muted hover:text-text-secondary transition-smooth"
                >
                  <Icon name="X" size={16} color="currentColor" />
                </button>
              </div>
            </form>
          )}
        </div>
        {/* Compact Suggestions with Vendor Listings */}
        {showSuggestions && (suggestions?.length > 0 || vendorListings?.length > 0 || isLoadingVendors) && isExpanded && (
          <div 
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-strong border border-border-light z-50 overflow-hidden animate-slide-down"
          >
            <div className="max-h-80 overflow-y-auto">
              {/* Category Suggestions */}
              {suggestions?.length > 0 && (
                <div className="p-1 border-b border-border-light">
                  <div className="text-xs font-medium text-text-secondary px-3 py-1">Categories</div>
                  <div className="max-h-48 overflow-y-auto">
                    {suggestions?.slice(0, 4)?.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md transition-smooth text-left ${
                          index === selectedIndex 
                            ? 'bg-primary-50 text-primary border-primary border' :'hover:bg-surface-100'
                        }`}
                      >
                        <Icon name={suggestion?.icon} size={14} color="#667eea" />
                        <span className="text-sm text-text-primary truncate">{suggestion?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Vendor Listings */}
              {(vendorListings?.length > 0 || isLoadingVendors) && (
                <div className="p-1">
                  <div className="text-xs font-medium text-text-secondary px-3 py-1">Vendors</div>
                  {isLoadingVendors ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span className="ml-2 text-xs text-text-secondary">Loading...</span>
                    </div>
                  ) : vendorError ? (
                    <div className="px-3 py-2 text-xs text-red-600 bg-red-50 rounded-md mx-1">
                      {vendorError}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {vendorListings?.slice(0, 3)?.map((vendor, index) => (
                        <div key={vendor?.id || index} className="px-1">
                          <VendorCard 
                            vendor={vendor} 
                            compact={true} 
                            showSaveButton={false} 
                            onToggleComparison={() => {}}
                            isSelectedForComparison={false}
                            canAddToComparison={true}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="w-full max-w-2xl" ref={searchRef}>
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative flex items-center bg-white rounded-lg shadow-soft border border-border overflow-hidden">
          <div className="flex items-center pl-4 pr-3">
            <Icon name="Search" size={20} color="#667eea" />
          </div>
          <Input
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 border-none bg-transparent placeholder:text-text-muted focus:ring-0 focus:outline-none py-3"
          />
          <div className="pr-4">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              iconName="Search"
              iconSize={16}
            >
              Search
            </Button>
          </div>
        </div>
      </form>
      {/* Default Suggestions with Vendor Listings */}
      {showSuggestions && (suggestions?.length > 0 || vendorListings?.length > 0 || isLoadingVendors) && (
        <div 
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-strong border border-border-light z-50 overflow-hidden animate-slide-down"
        >
          <div className="max-h-80 overflow-y-auto">
            {/* Category Suggestions */}
            {suggestions?.length > 0 && (
              <div className="p-2 border-b border-border-light">
                <div className="text-xs font-medium text-text-secondary px-3 py-1">Categories</div>
                <div className="max-h-48 overflow-y-auto">
                  {suggestions?.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-smooth text-left ${
                        index === selectedIndex 
                          ? 'bg-primary-50 text-primary border-primary border' :'hover:bg-surface-100'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-6 h-6 rounded ${
                        suggestion?.type === 'service' ? 'bg-primary-50 text-primary' : 'bg-secondary-50 text-secondary'
                      }`}>
                        <Icon name={suggestion?.icon} size={14} color="currentColor" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{suggestion?.label}</div>
                        <div className="text-xs text-text-secondary font-caption truncate">{suggestion?.cultural}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Vendor Listings */}
            {(vendorListings?.length > 0 || isLoadingVendors) && (
              <div className="p-2">
                <div className="text-xs font-medium text-text-secondary px-3 py-1">Vendors</div>
                {isLoadingVendors ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                    <span className="ml-2 text-sm text-text-secondary">Loading vendors...</span>
                  </div>
                ) : vendorError ? (
                  <div className="px-3 py-2 text-sm text-red-600 bg-red-50 rounded-md mx-1">
                    {vendorError}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {vendorListings?.map((vendor, index) => (
                      <div key={vendor?.id || index} className="px-1">
                        <VendorCard 
                          vendor={vendor} 
                          compact={true} 
                          showSaveButton={false} 
                          onToggleComparison={() => {}}
                          isSelectedForComparison={false}
                          canAddToComparison={true}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchHeader;